import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import { Box, Button, Chip, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";
import { DifficultyChip, EmptyState, SectionPaper } from "../components/shared";
import CourseCard from "../components/CourseCard";

type Props = {
  bookmarks: any[];
  courseBookmarks: any[];
  onLoadProblem: (id: string) => void;
  onOpenCourse?: (id: string) => void;
  onToggleBookmark: (id: string) => void;
  onToggleCourseBookmark?: (id: string) => void;
  onEnrollCourse?: (id: string) => void | Promise<void>;
};

export default function BookmarksView({
  bookmarks,
  courseBookmarks,
  onLoadProblem,
  onOpenCourse,
  onToggleBookmark,
  onToggleCourseBookmark,
  onEnrollCourse,
}: Props) {
  const [tab, setTab] = useState<"courses" | "problems">("courses");
  const problemCount = bookmarks.length;
  const courseCount = courseBookmarks.length;

  if (!problemCount && !courseCount) {
    return <EmptyState message="No bookmarks yet. Bookmark problems or courses to revisit them later." />;
  }

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ mb: 2, minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: 13, fontWeight: 600 } }}
      >
        <Tab
          value="courses"
          label={`Courses (${courseCount})`}
          icon={<AutoStoriesOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
        <Tab
          value="problems"
          label={`Problems (${problemCount})`}
          icon={<CodeOutlinedIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
        />
      </Tabs>

      {tab === "courses" && (
        courseCount === 0 ? (
          <EmptyState message="No bookmarked courses yet. Bookmark courses from the Courses tab." />
        ) : (
          <Grid container spacing={1.5}>
            {courseBookmarks.map((course) => {
              const id = String(course.id ?? course._id);
              const cardCourse = { ...course, id, bookmarked: true };
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={id} sx={{ display: "flex" }}>
                  <CourseCard
                    course={cardCourse}
                    onOpen={() => onOpenCourse?.(id)}
                    onEnroll={() => { void onEnrollCourse?.(id); }}
                    onToggleBookmark={() => { void onToggleCourseBookmark?.(id); }}
                  />
                </Grid>
              );
            })}
          </Grid>
        )
      )}

      {tab === "problems" && (
        problemCount === 0 ? (
          <EmptyState message="No bookmarked problems yet." />
        ) : (
          <Grid container spacing={1.5}>
            {bookmarks.map((b) => {
              const p = b.problemId ?? b;
              const id = p._id ?? p.id ?? b._id;
              return (
                <Grid item xs={12} sm={6} md={4} key={b._id ?? id}>
                  <SectionPaper>
                    <Box sx={{ p: 1.75 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "primary.main", mb: 0.5 }} noWrap>
                        {p.title ?? "Problem"}
                      </Typography>
                      <Stack direction="row" spacing={0.75} sx={{ mb: 1.25, flexWrap: "wrap" }}>
                        {p.difficulty && <DifficultyChip difficulty={p.difficulty} />}
                        {(p.tags ?? []).slice(0, 2).map((t: string) => (
                          <Chip key={t} size="small" label={t} sx={{ fontSize: "10px", height: "20px" }} />
                        ))}
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="contained" onClick={() => onLoadProblem(String(id))}
                          sx={{ textTransform: "none", fontSize: "12px", flex: 1 }}>{p.solved ? "Retake" : "Solve"}</Button>
                        <Button size="small" variant="outlined" color="error" onClick={() => onToggleBookmark(String(id))}
                          sx={{ textTransform: "none", fontSize: "12px" }}>Remove</Button>
                      </Stack>
                    </Box>
                  </SectionPaper>
                </Grid>
              );
            })}
          </Grid>
        )
      )}
    </Box>
  );
}
